import useConfirm from "@/hooks/useConfirm";
import { chargesKeys } from "@/query-keys";
import { services } from "@/services";
import type { ChargeListItem } from "@/schemas/chargeSchema";
import { createFileRoute } from "@tanstack/react-router";
import { List } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import { DataTable } from "@/components/datatable/components/DataTable";
import { tableSearchSchema, safeDecompress } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import useUserChargesQuery from "../_hooks/useUserChargesQuery";
import useChargesColumns from "../../charges/_hooks/useColumns";
import useChargeMutation from "../../charges/_hooks/useChargeMutation";

export const Route = createFileRoute("/_private/users/$userId/charges")({
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({ q: search.q }),
  loader: async ({ params, deps, context: { queryClient } }) => {
    const { userId } = params;
    const requestParams = mapTableStateToQueryParams(safeDecompress(deps.q));
    requestParams.set("createdBy", userId);

    const charges = await queryClient.ensureQueryData({
      queryKey: [...chargesKeys.lists(deps.q), "user", userId],
      queryFn: () => services.getCharges(requestParams),
    });

    return { charges };
  },
  component: UserChargesPage,
});

function UserChargesPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const { userId } = Route.useParams();
  const { charges: initialCharges } = Route.useLoaderData();
  const { data: charges } = useUserChargesQuery(userId, search.q, initialCharges);

  const columns = useChargesColumns();
  const { deleteCharge, isDeleting } = useChargeMutation();

  const deleteConfirm = useConfirm({
    title: t("charges.confirm.delete.title"),
    description: t("charges.confirm.delete.description"),
    isPending: isDeleting,
  });

  const handleDelete = useCallback(
    (row: ChargeListItem) => {
      deleteConfirm.confirm(() => {
        deleteCharge(row.id);
      });
    },
    [deleteConfirm, deleteCharge],
  );

  return (
    <div className="flex-1 flex flex-col gap-8">
      <div className="space-y-3">
        <div className="flex items-center gap-2 h-8">
          <List className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <h2 className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-medium">
            {t("users.detail.charges")}
          </h2>
        </div>

        <DataTable<ChargeListItem>
          queryKey={[...chargesKeys.lists(search.q), "user", userId]}
          data={charges?.items}
          nextCursor={charges?.nextCursor}
          hasMore={charges?.hasMore}
          columns={columns}
          searchPlaceholder={t("charges.list.searchPlaceholder")}
          navigateFrom="/users/$userId"
          searchFrom="/_private/users/$userId/charges"
          onDelete={handleDelete}
          searchable={true}
        />
      </div>

      {deleteConfirm.confirmDialog}
    </div>
  );
}
